import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../utils/formatters';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../../services/authService';

const GuaranteesWidget = ({ clientData, contractData, onChange }) => {
  const [policyData, setPolicyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customized, setCustomized] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalReason, setApprovalReason] = useState('');

  const [guarantees, setGuarantees] = useState({
    deposit_months: null,
    requires_guarantor: false
  });

  useEffect(() => {
    if (clientData && clientData.risk_level) {
      loadPolicyData(clientData.risk_level);
    }
  }, [clientData]);

  const loadPolicyData = async (riskLevel) => {
    try {
      setLoading(true);

      const response = await api.get(`/api/credit-policy/config/${riskLevel}`);
      const data = response.data;
      setPolicyData(data.data);

      // Inicializar com valores padrão
      setGuarantees({
        deposit_months: data.data.default_deposit_months,
        requires_guarantor: data.data.default_requires_guarantor === 1
      });

      onChange({
        deposit_months: data.data.default_deposit_months,
        requires_guarantor: data.data.default_requires_guarantor === 1
      });
    } catch (error) {
      console.error('Erro ao carregar política:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDepositChange = (value) => {
    const newValue = parseFloat(value);
    const isDifferent = policyData && newValue !== policyData.default_deposit_months;
    setCustomized(isDifferent || guarantees.requires_guarantor !== (policyData?.default_requires_guarantor === 1));
    setGuarantees({ ...guarantees, deposit_months: newValue });
    onChange({ ...guarantees, deposit_months: newValue });
  };

  const handleGuarantorChange = (value) => {
    const isDifferent = policyData && value !== (policyData.default_requires_guarantor === 1);
    setCustomized(isDifferent || guarantees.deposit_months !== policyData?.default_deposit_months);
    setGuarantees({ ...guarantees, requires_guarantor: value });
    onChange({ ...guarantees, requires_guarantor: value });
  };

  const handleResetToDefault = () => {
    if (policyData) {
      setGuarantees({
        deposit_months: policyData.default_deposit_months,
        requires_guarantor: policyData.default_requires_guarantor === 1
      });
      setCustomized(false);
      onChange({
        deposit_months: policyData.default_deposit_months,
        requires_guarantor: policyData.default_requires_guarantor === 1
      });
    }
  };

  const handleRequestApproval = async () => {
    if (!approvalReason.trim()) {
      toast.error('Por favor, informe a justificativa');
      return;
    }

    try {
      const response = await api.post('/api/credit-policy/override/request', {
        client_id: clientData.id,
        contract_id: contractData?.id || null,
        original_risk_level: clientData.risk_level,
        original_deposit_months: policyData.default_deposit_months,
        original_requires_guarantor: policyData.default_requires_guarantor,
        overridden_deposit_months: guarantees.deposit_months,
        overridden_requires_guarantor: guarantees.requires_guarantor ? 1 : 0,
        override_reason: approvalReason,
        requested_by: 1 // TODO: pegar do usuário logado
      });

      toast.success('Solicitação de aprovação enviada!');
      setShowApprovalModal(false);
      setApprovalReason('');
    } catch (error) {
      console.error('Erro ao solicitar aprovação:', error);
      const message = error.response?.data?.error || error.response?.data?.errors?.join(', ') || 'Erro ao solicitar aprovação';
      toast.error(message);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    }
  };

  const getRiskLevelLabel = (level) => {
    const labels = {
      'very_low': 'Muito Baixo',
      'low': 'Baixo',
      'medium': 'Médio',
      'high': 'Alto',
      'very_high': 'Muito Alto'
    };
    return labels[level] || level;
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      'very_low': 'text-green-600 dark:text-green-400',
      'low': 'text-blue-600 dark:text-blue-400',
      'medium': 'text-yellow-600 dark:text-yellow-400',
      'high': 'text-orange-600 dark:text-orange-400',
      'very_high': 'text-red-600 dark:text-red-400'
    };
    return colors[level] || colors.medium;
  };

  if (loading || !policyData || !clientData) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="text-center text-gray-600 dark:text-gray-400">
          {loading ? 'Carregando política de garantias...' : 'Selecione um cliente para ver as garantias'}
        </div>
      </div>
    );
  }

  // Calcular valor da caução
  const depositValue = guarantees.deposit_months && contractData?.monthly_rate
    ? (contractData.monthly_rate * guarantees.deposit_months)
    : 0;

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheckIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Garantias do Contrato
          </h3>
        </div>

        {/* Info do Cliente */}
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Cliente</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {clientData.name}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600 dark:text-gray-400">Risco de Crédito</p>
              <div className="flex items-center gap-2 justify-end">
                <span className={`text-lg font-bold ${getRiskLevelColor(clientData.risk_level)}`}>
                  {getRiskLevelLabel(clientData.risk_level)}
                </span>
                {clientData.credit_score && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    (Score: {clientData.credit_score})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Condições Padrão */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
            📋 Condições Padrão (baseadas no risco)
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-blue-800 dark:text-blue-300">Caução:</span>
              <span className="font-semibold text-blue-900 dark:text-blue-200">
                {policyData.default_deposit_months} mês{policyData.default_deposit_months > 1 ? 'es' : ''}
                {contractData?.monthly_rate && (
                  <span className="ml-2 text-blue-600 dark:text-blue-400">
                    ({formatCurrency((contractData.monthly_rate * policyData.default_deposit_months))})
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-800 dark:text-blue-300">Fiador:</span>
              <span className="font-semibold text-blue-900 dark:text-blue-200">
                {policyData.default_requires_guarantor ? 'Obrigatório' : 'Não necessário'}
              </span>
            </div>
          </div>
        </div>

        {/* Customizar Condições */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            ⚙️ Customizar Condições
            {customized && (
              <button
                onClick={handleResetToDefault}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
              >
                <ArrowPathIcon className="w-3 h-3" />
                Usar Padrão
              </button>
            )}
          </h4>

          {/* Caução */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Caução (meses de aluguel)
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleDepositChange(Math.max(0.5, guarantees.deposit_months - 0.5))}
                className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                −
              </button>
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="12"
                value={guarantees.deposit_months || ''}
                onChange={(e) => handleDepositChange(e.target.value)}
                className="w-24 px-4 py-2 text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold text-lg"
              />
              <button
                onClick={() => handleDepositChange(Math.min(12, guarantees.deposit_months + 0.5))}
                className="px-3 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                +
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">meses</span>
            </div>
            {contractData?.monthly_rate && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Valor estimado: <span className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(depositValue)}
                </span>
              </p>
            )}
          </div>

          {/* Fiador */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fiador/Avalista
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={guarantees.requires_guarantor}
                  onChange={() => handleGuarantorChange(true)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-900 dark:text-white">Obrigatório</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!guarantees.requires_guarantor}
                  onChange={() => handleGuarantorChange(false)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-900 dark:text-white">Dispensar</span>
              </label>
            </div>
          </div>
        </div>

        {/* Alerta se customizado */}
        {customized && policyData.override_requires_approval && (
          <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                  ⚠️ Atenção: Condições Fora do Padrão
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                  Você alterou as garantias obrigatórias. Esta mudança requer aprovação de um {policyData.approval_role === 'director' ? 'Diretor' : policyData.approval_role === 'manager' ? 'Gerente' : 'Administrador'}.
                </p>
                <button
                  onClick={() => setShowApprovalModal(true)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                >
                  Solicitar Aprovação
                </button>
              </div>
            </div>
          </div>
        )}

        {customized && !policyData.override_requires_approval && (
          <div className="mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-green-800 dark:text-green-200">
              <CheckCircleIcon className="w-5 h-5" />
              <span>Alterações permitidas sem necessidade de aprovação</span>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Justificativa */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Solicitar Aprovação de Exceção
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Por favor, justifique a alteração das condições padrão:
            </p>
            <textarea
              value={approvalReason}
              onChange={(e) => setApprovalReason(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows={4}
              placeholder="Ex: Cliente parceiro há 3 anos com bom histórico de pagamentos..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleRequestApproval}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Enviar Solicitação
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuaranteesWidget;
